import { useState, useMemo, useCallback } from 'react';
import { toast } from '@/utils/toast';
import { calculateDistance } from '@/utils/global';
export const useBranchesFilter = (allBranches, setSelectedBranches, locale) => {
  const CITY_ALL = locale === 'en' ? 'All Cities' : 'كل المدن';
  const DISTRICT_ALL = locale === 'en' ? 'All Districts' : 'كل الأحياء';

  // State management
  const [selectedCity, setSelectedCity] = useState(CITY_ALL);
  const [selectedDistrict, setSelectedDistrict] = useState(DISTRICT_ALL);
  const [searchInput, setSearchInput] = useState('');

  // Calculate distance between two coordinates

  // Extract all unique cities from branches
  const cities = useMemo(() => {
    const uniqueCities = [
      ...new Set(
        allBranches.map((branch) => branch.source_region_name)?.filter((e) => e)
      ),
    ];
    return [CITY_ALL, ...uniqueCities];
  }, [allBranches, CITY_ALL]);

  // Extract districts based on selected city
  const districts = useMemo(() => {
    if (selectedCity === CITY_ALL) {
      const allDistricts = [
        ...new Set(allBranches.map((branch) => branch.source_city_name)),
      ];
      return [DISTRICT_ALL, ...allDistricts];
    }

    const cityBranches = allBranches.filter(
      (branch) => branch.source_region_name === selectedCity
    );
    const uniqueDistricts = [
      ...new Set(cityBranches.map((branch) => branch.source_city_name)),
    ];
    return [DISTRICT_ALL, ...uniqueDistricts];
  }, [selectedCity, allBranches, DISTRICT_ALL]);

  // Handle city change
  const handleCityChange = useCallback(
    (selectedOption) => {
      const newCity = selectedOption?.value || CITY_ALL;
      setSelectedCity(newCity);
      setSelectedDistrict(DISTRICT_ALL);

      // Filter branches based on selected city
      if (newCity === CITY_ALL) {
        setSelectedBranches(allBranches);
      } else {
        const filteredBranches = allBranches?.filter(
          (branch) => branch.source_region_name === newCity
        );
        setSelectedBranches(filteredBranches);
      }
    },
    [allBranches, setSelectedBranches, CITY_ALL, DISTRICT_ALL]
  );

  // Handle district change
  const handleDistrictChange = useCallback(
    (selectedOption) => {
      const newDistrict = selectedOption?.value || DISTRICT_ALL;
      setSelectedDistrict(newDistrict);

      let filteredBranches = allBranches;

      // First filter by city
      if (selectedCity !== CITY_ALL) {
        filteredBranches = filteredBranches.filter(
          (branch) => branch.source_region_name === selectedCity
        );
      }

      // Then filter by district
      if (newDistrict !== DISTRICT_ALL) {
        filteredBranches = filteredBranches.filter(
          (branch) => branch.source_city_name === newDistrict
        );
      }

      setSelectedBranches(filteredBranches);
    },
    [selectedCity, allBranches, setSelectedBranches, CITY_ALL, DISTRICT_ALL]
  );

  // Handle search
  const handleSearch = useCallback(
    (e) => {
      e.preventDefault();

      let filteredBranches = allBranches;

      // Filter by city
      if (selectedCity !== CITY_ALL) {
        filteredBranches = filteredBranches.filter(
          (branch) => branch.source_region_name === selectedCity
        );
      }

      // Filter by district
      if (selectedDistrict !== DISTRICT_ALL) {
        filteredBranches = filteredBranches.filter(
          (branch) => branch.source_city_name === selectedDistrict
        );
      }

      // Apply search filter
      if (searchInput.trim()) {
        const searchTerm = searchInput.trim().toLowerCase();
        filteredBranches = filteredBranches.filter(
          (branch) =>
            branch?.address?.toLowerCase().includes(searchTerm) ||
            branch?.code?.toLowerCase().includes(searchTerm) ||
            branch?.source_city_name?.toLowerCase().includes(searchTerm) ||
            branch?.name?.toLowerCase().includes(searchTerm) ||
            branch?.source_region_name?.toLowerCase().includes(searchTerm)
        );
      }

      setSelectedBranches(filteredBranches);
    },
    [
      selectedCity,
      selectedDistrict,
      searchInput,
      allBranches,
      setSelectedBranches,
      CITY_ALL,
      DISTRICT_ALL,
    ]
  );

  // Handle reset filters
  const handleReset = useCallback(() => {
    setSelectedCity(CITY_ALL);
    setSelectedDistrict(DISTRICT_ALL);
    setSearchInput('');
    setSelectedBranches(allBranches);
  }, [allBranches, setSelectedBranches, CITY_ALL, DISTRICT_ALL]);

  // Handle nearest to me
  const handleNearestToMe = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error(
        locale === 'en'
          ? 'Geolocation is not supported by your browser'
          : 'المتصفح لا يدعم تحديد الموقع'
      );
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLoc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        // Calculate distance for each branch and sort by distance
        const branchesWithDistance = allBranches?.map((branch) => ({
          ...branch,
          distance: calculateDistance(
            userLoc.lat,
            userLoc.lng,
            branch.lat,
            branch.lng
          ),
        }));

        // Sort branches by distance (nearest first)
        const sortedBranches = branchesWithDistance.sort(
          (a, b) => a.distance - b.distance
        );

        // Update the branches list with sorted results
        setSelectedBranches(sortedBranches);
      },
      (error) => {
        let errorMessage =
          locale === 'en'
            ? 'Unable to retrieve your location'
            : 'تعذّر تحديد موقعك';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage =
              locale === 'en'
                ? 'Location access denied. Please enable location permissions.'
                : 'تم رفض الوصول إلى الموقع. يرجى تمكين أذونات الموقع.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage =
              locale === 'en'
                ? 'Location information is unavailable.'
                : 'معلومات الموقع غير متوفرة.';
            break;
          case error.TIMEOUT:
            errorMessage =
              locale === 'en'
                ? 'Location request timed out.'
                : 'انتهت مهلة طلب الموقع.';
            break;
        }

        toast.error(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  }, [allBranches, setSelectedBranches, calculateDistance, locale]);

  // Prepare options for ReactSelect
  const cityOptions = useMemo(
    () =>
      cities.map((city) => ({
        label: city,
        value: city,
      })),
    [cities]
  );

  const districtOptions = useMemo(
    () =>
      districts.map((district) => ({
        label: district,
        value: district,
      })),
    [districts]
  );

  return {
    // State
    selectedCity,
    selectedDistrict,
    searchInput,

    // Setters
    setSearchInput,

    // Options
    cityOptions,
    districtOptions,

    // Handlers
    handleCityChange,
    handleDistrictChange,
    handleSearch,
    handleReset,
    handleNearestToMe,

    // Constants
    CITY_ALL,
  };
};
